import useRequest from "@/lib/request/useRequest";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { IrrigationOperation } from "@/types/operations";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { object, date, number, string, array, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import DateInput from "@/components/inputs/DateInput";
import NumberInputComponent from "@/components/inputs/NumberInput";
import TextAreaInput from "@/components/inputs/TextAreaInput";
import SelectMultipleInput from "@/components/inputs/SelectMultipleInput";
import { Skeleton, Button } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "@tanstack/react-router";
import { roundTo } from "@/lib/utils/numbers";
import { normalizeIrrigationSystemIds } from "@/lib/utils/irrigation";
import { getIrrigationSystemDisplayName } from "@/types/irrigationSystem";

const schema = object().shape({
    start_date: date().required(),
    depth: number().required().min(0),
    note: string().nullable(),
    irrigation_systems: array().of(number().required()).min(1).required(),
});

export type IrrigationOperationFormData = InferType<typeof schema>;

function IrrigationOperationForm({
    existingIrrigationId,
}: {
    existingIrrigationId?: number;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const existingIrrigationQuery = useGetRequest<IrrigationOperation>({
        url: `irrigation_operations/${existingIrrigationId}/`,
        enabled: !!existingIrrigationId,
    });
    const form = useForm<IrrigationOperationFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            irrigation_systems: [],
        },
    });
    const { t } = useTranslation("operations");
    const { formatUnit } = useI18n();
    const { fieldId, fieldQuery, selectedCropZone } = useFieldDashboard() ?? {};
    const { setValue, watch } = form;
    const irrigationSystemIds = watch("irrigation_systems");

    const fieldIrrigationSystems = useMemo(() => {
        return fieldQuery?.data?.irrigation_systems ?? [];
    }, [fieldQuery?.data?.irrigation_systems]);

    const selectedIrrigationSystemIds = useMemo(() => {
        return normalizeIrrigationSystemIds(irrigationSystemIds);
    }, [irrigationSystemIds]);

    const irrigationSystemOptions = useMemo(() => {
        return fieldIrrigationSystems.map((system, index) => ({
            id: system.id,
            description: getIrrigationSystemDisplayName(
                system,
                t("irrigation_system.unnamed", {
                    ns: "setup",
                    number: index + 1,
                }),
            ),
        }));
    }, [fieldIrrigationSystems, t]);

    useEffect(() => {
        if (existingIrrigationQuery.data) {
            const systems = normalizeIrrigationSystemIds(
                existingIrrigationQuery.data.irrigation_systems?.length
                    ? existingIrrigationQuery.data.irrigation_systems
                    : existingIrrigationQuery.data.irrigation_system,
            );
            form.reset({
                start_date: dayjs(
                    existingIrrigationQuery.data.start_date,
                ).toDate(),
                depth: roundTo(
                    existingIrrigationQuery.data.average_depth ?? 0,
                    2,
                ),
                note: existingIrrigationQuery.data.note,
                irrigation_systems: systems,
            });
            return;
        }
        form.reset({
            start_date: new Date(),
            depth: undefined,
            note: undefined,
            irrigation_systems: [],
        });
    }, [existingIrrigationQuery.data, form]);

    useEffect(() => {
        if (!fieldIrrigationSystems.length) return;
        if (fieldIrrigationSystems.length === 1) {
            const onlyId = fieldIrrigationSystems[0].id;
            if (
                selectedIrrigationSystemIds.length === 1 &&
                selectedIrrigationSystemIds[0] === onlyId
            ) {
                return;
            }
            setValue("irrigation_systems", [onlyId]);
            return;
        }
        if (existingIrrigationId) return;
        if (selectedIrrigationSystemIds.length) return;
        setValue(
            "irrigation_systems",
            fieldIrrigationSystems.map((system) => system.id),
        );
    }, [
        existingIrrigationId,
        selectedIrrigationSystemIds,
        fieldIrrigationSystems,
        setValue,
    ]);

    const saveOperationRequest = useMutateRequest({
        errorMessage: t("error.failed_to_upload_operation"),
        successMessage: t("success.operation_uploaded_successfully"),
    });

    const saveOperation = (data: IrrigationOperationFormData) => {
        const systems = normalizeIrrigationSystemIds(data.irrigation_systems);
        const payload = {
            id: existingIrrigationId,
            field: fieldId,
            crop_zone:
                existingIrrigationQuery.data?.crop_zone ??
                selectedCropZone?.id ??
                null,
            start_date: dayjs(data.start_date).format("YYYY-MM-DD"),
            irrigated_rates: [{ irrigated: true, rate: data.depth }],
            note: data.note === "" ? null : data.note,
            irrigation_systems: systems,
            irrigation_system: systems[0] ?? null,
        };
        const method = existingIrrigationId ? "PUT" : "POST";
        saveOperationRequest.mutate(
            {
                url: existingIrrigationId
                    ? `irrigation_operations/${existingIrrigationId}/`
                    : "irrigation_operations/",
                method,
                data: payload,
            },
            {
                onSuccess: () => {
                    navigate({
                        to: "/dashboard/fields/$fieldId/operations",
                        params: {
                            fieldId: fieldId as string,
                        },
                    });
                },
            },
        );
    };

    return existingIrrigationQuery.isLoading ? (
        <Skeleton className="h-40 w-full rounded-lg" />
    ) : (
        <FormProvider {...form}>
            <form>
                <div className="flex flex-col gap-2 text-sm">
                    <DateInput
                        name="start_date"
                        label={t("date")}
                        variant="flat"
                        isRequired
                        className="w-full"
                        size="sm"
                        labelPlacement="outside"
                        showMonthAndYearPickers
                    />
                    {fieldIrrigationSystems.length > 1 && (
                        <SelectMultipleInput
                            name="irrigation_systems"
                            options={irrigationSystemOptions}
                            label={t("irrigation_systems")}
                            size="sm"
                            labelPlacement="outside"
                            placeholder={t("irrigation_systems")}
                            className="w-full"
                            isRequired
                        />
                    )}
                    <NumberInputComponent
                        name="depth"
                        label={`${t("depth")} (${formatUnit("in", "cm")})`}
                        variant="flat"
                        size="sm"
                        placeholder={t("depth")}
                        className="w-full"
                        min={0}
                        step={0.1}
                        isRequired
                        labelPlacement="outside"
                    />
                    <TextAreaInput
                        className="flex-1"
                        name="note"
                        label={t("note")}
                        variant="flat"
                        size="sm"
                        placeholder={t("note")}
                        labelPlacement="outside"
                    />
                    <Button
                        fullWidth
                        color="secondary"
                        onPress={() => {
                            form.trigger();
                            form.handleSubmit(saveOperation)();
                        }}
                        isLoading={saveOperationRequest.isPending}
                    >
                        <FontAwesomeIcon
                            icon={existingIrrigationId ? faSave : faPlusCircle}
                            className="mr-2"
                        />
                        {existingIrrigationId
                            ? t("saveOperation")
                            : t("addOperation")}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
}
export default IrrigationOperationForm;
